import { TypeChecker } from '@attain-apps/type-guards'

import type { Action } from './action'
import type * as condition from './condition'
import { Effect } from './effect'
import type { Urn } from './urn'

/**
 * The unit of permission, shared by roles and direct policies.
 *
 * @see {@link Statement.Concrete}
 * @see {@link Statement.Templated}
 */
export interface Statement<TResource extends Urn.Template = Urn.Pattern> {
  /** Optional label; appears in audit and decision logs */
  readonly sid?: string
  readonly effect: Effect
  readonly actions: readonly Action.Pattern[]
  readonly resources: readonly TResource[]
  readonly conditions?: Statement.Conditions
}
export namespace Statement {
  /** A direct policy's statements, or a role's after substitution */
  export type Concrete = Statement<Urn.Pattern>
  /**
   * A role's statements, before an assignment supplies its scope.
   *
   * @example
   * { effect: 'allow', actions: ['admin:zone:*'], resources: ['atn:tenant_${scope.tenant}:*'] }
   */
  export type Templated = Statement<Urn.Template>

  /**
   * "This action, but only when…": the statement matches only if every
   * condition holds.
   *
   * @example
   * { resourceOwner: { equals: '${principal.id}' } }
   */
  export type Conditions = Readonly<
    Partial<Record<condition.Key, condition.Clause>>
  >

  /**
   * Caps that keep one grant's write inside a single DynamoDB transaction:
   * `rows written + Σ(statements × resources) ≤ 99`.
   *
   * TODO: confirm the per-statement split once real roles are drafted
   */
  export const MAX_STATEMENTS = 20
  export const MAX_RESOURCES_PER_STATEMENT = 20
  export const MAX_TRANSACTION_ITEMS = 99

  /**
   * A {@link Statement} as a caller sends it, before the backend validator
   * turns it into a {@link Concrete} or {@link Templated} one.
   */
  export interface Input
    extends Omit<Statement, 'actions' | 'resources' | 'conditions'> {
    // Caller input for the fields the validator narrows into Statement's types
    readonly actions: readonly string[]
    readonly resources: readonly string[]
    readonly conditions?: Readonly<Record<string, unknown>>
  }
  export namespace Input {
    /**
     * Shape only: does not check the registries, URN grammar, wildcards or
     * variables, and does not reject unknown keys
     */
    export const isInput: TypeChecker<Input> =
      TypeChecker.Factory.object<Input>({
        actions: TypeChecker.Factory.list(TypeChecker.isString),
        conditions: TypeChecker.Factory.optional(
          TypeChecker.isObject as TypeChecker<
            Readonly<Record<string, unknown>>
          >,
        ),
        effect: Effect.isEffect,
        resources: TypeChecker.Factory.list(TypeChecker.isString),
        sid: TypeChecker.Factory.optional(TypeChecker.isString),
      }).usingAlias('policy.Statement.Input')
  }
}
